import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import UpsertProcedureForm from 'components/procedures/UpsertProcedureForm';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Modal } from 'semantic-ui-react';
import { deleteProcedure, findProcedureById, searchProcedure } from 'services/procedureServices';
import { getAllTariffs } from 'services/tariffServices';
import { debounce } from 'ts-debounce';
import { ProcedureDto, ProcedureSearchRequest, TariffDto, UpsertProcedureDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const initialProcedureSearch: Partial<ProcedureSearchRequest> = {};

const numberFormatter = Intl.NumberFormat('de-CH', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2
});

interface ProcedureFieldProps extends FormState {
  values: Partial<ProcedureSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundProcedures: Array<ProcedureDto>,
  proceduresLoaded: boolean,
  procedure?: UpsertProcedureDto,
  procedureId?: number,
  addEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  tariffs: Array<TariffDto>,
  selectedTariffId?: number,
  searchValues: Partial<ProcedureSearchRequest>,
  tarmedTariff?: Boolean
}

class ProceduresView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundProcedures: [],
      proceduresLoaded: false,
      procedure: undefined,
      procedureId: undefined,
      addEditModalOpen: false,
      tariffs: [],
      selectedTariffId: undefined,
      searchValues: initialProcedureSearch,
      tarmedTariff: false,
      cancelTokenSource
    };

    getAllTariffs(cancelTokenSource)
        .then(response => {
          const tariffId = response[0].lnkTrfId != null ? response[0].lnkTrfId : response[0].id;
          this.setState({
            tariffs: response,
            selectedTariffId: tariffId,
            tarmedTariff: response[0].tarmedTariff
          })
        }).catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findProcedureById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, procedure?: UpsertProcedureDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        procedure: procedure,
        procedureId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      procedureId: undefined,
      procedure: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;
    const values = {...searchValues, selection: 1}

    const onFinally = () => {
      this.setState({
        proceduresLoaded: true
      })
    };

    searchProcedure(values, cancelTokenSource)
        .then(response => {
          this.setState({
            foundProcedures: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteProcedure = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteProcedure(id, cancelTokenSource).then(response => {
      this.setState(({foundProcedures}: State) => ({
        foundProcedures: foundProcedures.filter((procedure: ProcedureDto) => procedure.id != response.id)
      }))
    }).catch(noop)
  };

  handleProcedureChange = debounce(({values}: ProcedureFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource, tariffs} = this.state;

    let valuesCopy = {
      ...values,
      searchInLinkedTariff: false,
      code: values.description
    };
    if (values.selection) {
      valuesCopy.selection = 1;
    } else {
      delete valuesCopy.selection;
    }
    if (values.tariffId != null) {
      this.setState({proceduresLoaded: false});

      const tariff = tariffs.find((tr) => {
        return tr.id == values.tariffId;
      });

      let tariffId = values.tariffId;
      // if (tariff) {
      //   tariffId = tariff!.lnkTrfId != null ? tariff!.lnkTrfId : tariff!.id;
      // }

      const onFinally = () => {
        this.setState({
          proceduresLoaded: true
        })
      };

      searchProcedure(valuesCopy, cancelTokenSource)
          .then(response =>
              this.setState({
                foundProcedures: response,
                selectedTariffId: tariffId,
                tarmedTariff: tariff ? tariff.tarmedTariff : undefined,
                searchValues: values
              })
          )
          .catch(noop)
          .finally(onFinally);
    }

  }, 300);

  render(): React.ReactNode {
    const {proceduresLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchProcedureSegment()}
          </div>
          <StyledViewTableRow>
            {!proceduresLoaded ?
                <LoaderComponent message={getMessage("procedure.loading")}/> : this.renderProceduresSegment()}
          </StyledViewTableRow>
          {this.renderUpsertProcedureModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchProcedureSegment = (): JSX.Element => {

    const {tariffs} = this.state;

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffId: tariffs.length > 0 ? tariffs[0].id : null
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;

    const {tariffs, foundProcedures} = this.state;
    const tariffCodeOptions = tariffs.map(tariffCode => {

      return {
        key: (tariffCode.id),
        text: tariffCode.description,
        value: tariffCode.id
      }
    });

    const numResults = foundProcedures.length;

    return (

        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffId"
                  component={Select}
                  fluid
                  options={tariffCodeOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  fluid
                  component={Input}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle"></Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="selection"
                  fluid
                  component={CheckBox}
                  label={getMessage("procedure.selected-only")}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleProcedureChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("procedure.confirmDelete")}
              onConfirm={() => this.deleteProcedure(cellData)}
          />
        </React.Fragment>
    );
  };

  renderValueCell = ({cellData}: any) => {
    return numberFormatter.format(cellData);
  };

  proceduresRowGetter = ({index}: any) => {
    const {foundProcedures} = this.state;

    Object.assign(foundProcedures[index], {index: index + 1});

    return foundProcedures[index]
  };

  foundProceduresRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundProcedures} = this.state;

    const procedureId = foundProcedures[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(procedureId)}
        >
          {columns}
        </div>
    );
  };

  renderProceduresSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundProcedures} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundProcedures.length}
            rowGetter={this.proceduresRowGetter}
            rowRenderer={this.foundProceduresRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 250,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 250,
                label: (getMessage("value")),
                dataKey: "value",
                cellRenderer: (this.renderValueCell)
              },
              {
                width: 200,
                label: (getMessage("type")),
                dataKey: "type"
              },
              {
                width: 1600,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderUpsertProcedureModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, procedure, procedureId, selectedTariffId, tarmedTariff} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("procedures")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertProcedureForm
                procedure={procedure}
                procedureId={procedureId}
                tariff={selectedTariffId}
                tarmedTariff={tarmedTariff}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

export default withMessages(ProceduresView);
